import { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";

const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export const centsToDollars = (cents) => Number(cents || 0) / 100;

export const fmtCents = (cents) => moneyFormatter.format(centsToDollars(cents));

export function useNoteDetail(noteId, hid) {
  const year = useMemo(() => new Date().getFullYear(), []);
  const [bundle, setBundle] = useState(null);
  const [tax1098, setTax1098] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reloadCounter, setReloadCounter] = useState(0);

  useEffect(() => {
    if (!noteId || !hid) {
      setBundle(null);
      setTax1098(null);
      setDocuments([]);
      setLoading(false);
      setError(noteId ? "Household context is required to load note details." : "");
      return;
    }

    let cancelled = false;
    const encodedId = encodeURIComponent(noteId);

    setLoading(true);
    setError("");

    Promise.all([
      api.get(`/notes/${encodedId}?household_id=${encodeURIComponent(hid)}`),
      api.get(`/notes/${encodedId}/1098?year=${year}&household_id=${encodeURIComponent(hid)}`),
      api.get(`/notes/${encodedId}/documents?household_id=${encodeURIComponent(hid)}`),
    ])
      .then(([bundleRes, taxRes, docsRes]) => {
        if (cancelled) return;
        setBundle(bundleRes.data);
        setTax1098(taxRes.data);
        setDocuments(Array.isArray(docsRes.data) ? docsRes.data : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setBundle(null);
        setTax1098(null);
        setDocuments([]);
        setError(e?.response?.data?.detail || "Failed to load note details.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [hid, noteId, reloadCounter, year]);

  const reload = () => setReloadCounter((count) => count + 1);

  const documentHref = (docId) => {
    if (!noteId || !docId || !hid) return "#";
    return `${API}/notes/${encodeURIComponent(noteId)}/documents/${encodeURIComponent(docId)}/file?household_id=${encodeURIComponent(hid)}`;
  };

  return {
    bundle,
    tax1098,
    documents,
    loading,
    error,
    year,
    centsToDollars,
    fmtCents,
    documentHref,
    reload,
  };
}
